import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useOverlay } from '../context/OverlayContext';
import GameSetupOverlay from '../components/GameSetupOverlay';
import { refreshToken, trackerFetch } from '../utils/auth';

const initialMistakes = {
  ace: 0,
  winner: 0,
  serveFault: 0,
  firstServeFault: 0,
  doubleFault: 0,
  unforcedError: 0,
  forcedError: 0,
};

const serveEvents = [
  { key: 'serveFault', label: 'Serve Fault', serverOnly: true, isSpecialServe: true },
];

const positiveEvents = [
  { key: 'ace', label: 'Ace', serverOnly: true, isPositive: true },
  { key: 'winner', label: 'Winner', serverOnly: false, isPositive: true },
];

const negativeEvents = [
  { key: 'unforcedError', label: 'Unforced Error', serverOnly: false, isPositive: false },
  { key: 'forcedError', label: 'Forced Error', serverOnly: false, isPositive: false },
];

const allMistakeTypes = [...serveEvents, ...positiveEvents, ...negativeEvents];

const MatchTrackerPage = () => {
  const { matchId } = useParams();
  const navigate = useNavigate();
  const { showOverlay } = useOverlay();

  const [loading, setLoading] = useState(true);
  const [matchData, setMatchData] = useState(null);
  const [currentState, setCurrentState] = useState({
    setNumber: 1,
    gameNumber: 1,
    myGamesWonInSet: 0,
    opponentGamesWonInSet: 0,
    mySetsWon: 0,
    opponentSetsWon: 0,
    serving: 'me',
    initialSetServer: 'me',
    matchScores: [],
    matchCompleted: false,
  });

  const [serveAttempt, setServeAttempt] = useState(1); // 1 = 1st serve, 2 = 2nd serve
  const [myMistakes, setMyMistakes] = useState({ ...initialMistakes });
  const [opponentMistakes, setOpponentMistakes] = useState({ ...initialMistakes });
  const [showGameSetupOverlay, setShowGameSetupOverlay] = useState(false);
  const [pointState, setPointState] = useState({ me: 0, opp: 0 });

  // Finished Games & Inline Edit Modal
  const [completedGames, setCompletedGames] = useState([]);
  const [editingGameIndex, setEditingGameIndex] = useState(null);
  const [editingGame, setEditingGame] = useState(null);

  // In-Game Point Adjust Overlay State
  const [showPointAdjustOverlay, setShowPointAdjustOverlay] = useState(false);
  const [tempPoints, setTempPoints] = useState({ me: 0, opp: 0 });

  // Synchronous ref to hold latest tracking state
  const latestStateRef = useRef({
    currentState: {
      setNumber: 1,
      gameNumber: 1,
      myGamesWonInSet: 0,
      opponentGamesWonInSet: 0,
      mySetsWon: 0,
      opponentSetsWon: 0,
      serving: 'me',
      initialSetServer: 'me',
      matchScores: [],
      matchCompleted: false,
    },
    pointState: { me: 0, opp: 0 },
    myMistakes: { ...initialMistakes },
    opponentMistakes: { ...initialMistakes },
    serveAttempt: 1,
    completedGames: [],
    matchData: null,
  });

  // Keep latestStateRef updated on any React state change
  useEffect(() => {
    latestStateRef.current = {
      currentState,
      pointState,
      myMistakes,
      opponentMistakes,
      serveAttempt,
      completedGames,
      matchData,
    };
  }, [currentState, pointState, myMistakes, opponentMistakes, serveAttempt, completedGames, matchData]);

  // Session Keep-Alive while on Match Tracker: continuously refresh token every 10 mins and on focus/resume
  useEffect(() => {
    const intervalId = setInterval(() => {
      refreshToken();
    }, 10 * 60 * 1000); // 10 minutes

    const handleResume = () => {
      if (document.visibilityState === 'visible') {
        refreshToken();
      }
    };

    document.addEventListener('visibilitychange', handleResume);
    window.addEventListener('focus', handleResume);

    return () => {
      clearInterval(intervalId);
      document.removeEventListener('visibilitychange', handleResume);
      window.removeEventListener('focus', handleResume);
    };
  }, []);

  const normalizeMistakes = useCallback((raw) => {
    if (!raw || typeof raw !== 'object') return {};
    const normalized = {};
    for (const [k, v] of Object.entries(raw)) {
      const val = parseInt(v, 10) || 0;
      if (val === 0) continue;
      if (k === 'unforced_error' || k === 'unforcedError') normalized.unforcedError = val;
      else if (k === 'forced_error' || k === 'forcedError') normalized.forcedError = val;
      else if (k === 'double_fault' || k === 'doubleFault') normalized.doubleFault = val;
      else if (k === 'first_serve_fault' || k === 'serve_fault' || k === 'firstServeFault' || k === 'serveFault') normalized.serveFault = val;
      else if (k === 'ace') normalized.ace = val;
      else if (k === 'winner') normalized.winner = val;
      else normalized[k] = val;
    }
    return normalized;
  }, []);

  const extractCompletedGames = useCallback((match) => {
    if (!match) return [];
    if (Array.isArray(match.completedGames) && match.completedGames.length > 0) {
      return match.completedGames.map((g) => ({
        ...g,
        myMistakes: normalizeMistakes(g.myMistakes),
        opponentMistakes: normalizeMistakes(g.opponentMistakes),
      }));
    }
    const list = [];
    if (Array.isArray(match.sets)) {
      match.sets.forEach((s) => {
        if (Array.isArray(s.games)) {
          s.games.forEach((g) => {
            const isMe = g.winner === 'me' || g.winner === 'player1';
            const losScore = g.losingScore || g.losing_score || '0';
            const defaultPtScore = isMe ? `40 - ${losScore}` : `${losScore} - 40`;

            const p1MD = g.mistake_details?.player1_mistakes_details || g.player1_mistakes_details || g.myMistakes || g.my_mistakes || {};
            const p2MD = g.mistake_details?.player2_mistakes_details || g.player2_mistakes_details || g.opponentMistakes || g.opponent_mistakes || {};

            list.push({
              id: g.game_id || `game_${s.set_number}_${g.game_number}`,
              setNumber: parseInt(s.set_number, 10) || 1,
              gameNumber: parseInt(g.game_number, 10) || 1,
              winner: isMe ? 'me' : 'opponent',
              servingPlayer: g.serving_player,
              myMistakes: normalizeMistakes(p1MD),
              opponentMistakes: normalizeMistakes(p2MD),
              pointScore: g.pointScore || g.point_score || defaultPtScore,
              losingScore: losScore,
            });
          });
        }
      });
    }
    return list;
  }, [normalizeMistakes]);

  const recalculateScoresFromGames = useCallback((gamesList) => {
    if (!Array.isArray(gamesList) || gamesList.length === 0) {
      return {
        setNumber: 1,
        gameNumber: 1,
        myGamesWonInSet: 0,
        opponentGamesWonInSet: 0,
        mySetsWon: 0,
        opponentSetsWon: 0,
        matchScores: [],
      };
    }

    const setsMap = {};
    gamesList.forEach((g) => {
      const sn = parseInt(g.setNumber, 10) || 1;
      if (!setsMap[sn]) setsMap[sn] = { myGames: 0, oppGames: 0, gameCount: 0 };
      if (g.winner === 'me' || g.winner === 'player1') {
        setsMap[sn].myGames += 1;
      } else {
        setsMap[sn].oppGames += 1;
      }
      setsMap[sn].gameCount += 1;
    });

    const sortedSetNumbers = Object.keys(setsMap).map(Number).sort((a, b) => a - b);
    const currentSetNum = sortedSetNumbers.length > 0 ? Math.max(...sortedSetNumbers) : 1;
    const currentSetData = setsMap[currentSetNum] || { myGames: 0, oppGames: 0, gameCount: 0 };

    let mySetsWon = 0;
    let oppSetsWon = 0;
    const matchScores = [];

    sortedSetNumbers.forEach((sn) => {
      const s = setsMap[sn];
      if (sn < currentSetNum) {
        matchScores.push({ myGames: s.myGames, opponentGames: s.oppGames });
        if (s.myGames > s.oppGames) mySetsWon += 1;
        else if (s.oppGames > s.myGames) oppSetsWon += 1;
      }
    });

    return {
      setNumber: currentSetNum,
      gameNumber: currentSetData.gameCount + 1,
      myGamesWonInSet: currentSetData.myGames,
      opponentGamesWonInSet: currentSetData.oppGames,
      mySetsWon,
      opponentSetsWon: oppSetsWon,
      matchScores,
    };
  }, []);

  const saveTimeoutRef = useRef(null);

  const flushSave = useCallback((override = {}, immediate = false) => {
    if (!matchId) return;

    const data = {
      ...latestStateRef.current,
      ...override,
    };

    const curState = data.currentState || currentState;
    const curPoints = data.pointState || pointState;
    const curMyM = data.myMistakes || myMistakes;
    const curOppM = data.opponentMistakes || opponentMistakes;
    const curServe = data.serveAttempt !== undefined ? data.serveAttempt : serveAttempt;

    const fullStateToSave = {
      ...curState,
      pointState: curPoints,
      activeGameMistakes: { me: curMyM, opponent: curOppM },
      serveAttempt: curServe,
      updated_at: Date.now(),
    };

    // Save synchronously to local storage cache for instant browser restore & zero lag
    try {
      localStorage.setItem(`local_active_match_${matchId}`, JSON.stringify(fullStateToSave));
    } catch (e) {}

    // Do not sync in-game state if the match is already completed or if there's no auth token
    if (curState.matchCompleted || (matchData && (matchData.status === 'completed' || matchData.status === 'finished'))) {
      return;
    }

    // Debounce remote background sync to avoid spamming the backend on every button tap
    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current);
      saveTimeoutRef.current = null;
    }

    const doRemoteSync = () => {
      if (curState.matchCompleted) return;
      trackerFetch('update_match_state.php', {
        method: 'POST',
        body: JSON.stringify({
          matchId,
          current_match_state: fullStateToSave,
        }),
      }).catch(() => {});
    };

    if (immediate) {
      doRemoteSync();
    } else {
      saveTimeoutRef.current = setTimeout(doRemoteSync, 2500);
    }
  }, [matchId, currentState, pointState, myMistakes, opponentMistakes, serveAttempt, matchData]);

  const persistInGameState = useCallback((customState = null, customPoints = null, customMyM = null, customOppM = null, customServe = null) => {
    const override = {};
    if (customState) override.currentState = customState;
    if (customPoints !== null) override.pointState = customPoints;
    if (customMyM) override.myMistakes = customMyM;
    if (customOppM) override.opponentMistakes = customOppM;
    if (customServe !== null) override.serveAttempt = customServe;

    latestStateRef.current = {
      ...latestStateRef.current,
      ...override,
    };

    flushSave(override, false);
  }, [flushSave]);

  // Hook beforeunload, visibilitychange and component unmount to ensure immediate save
  useEffect(() => {
    const handleBeforeUnload = () => {
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
        saveTimeoutRef.current = null;
      }
      const latest = latestStateRef.current || {};
      const fullStateToSave = {
        ...(latest.currentState || {}),
        pointState: latest.pointState || { me: 0, opp: 0 },
        activeGameMistakes: { me: latest.myMistakes || {}, opponent: latest.opponentMistakes || {} },
        serveAttempt: latest.serveAttempt || 1,
        updated_at: Date.now(),
      };
      try {
        localStorage.setItem(`local_active_match_${matchId}`, JSON.stringify(fullStateToSave));
      } catch (e) {}
      trackerFetch('update_match_state.php', {
        method: 'POST',
        body: JSON.stringify({
          matchId,
          current_match_state: fullStateToSave,
        }),
      }).catch(() => {});
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'hidden') {
        handleBeforeUnload();
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      handleBeforeUnload();
    };
  }, [matchId]);

  const loadMatch = useCallback(async () => {
    if (!matchId) {
      showOverlay('No match selected. Redirecting to create a new match.', 'error', () => navigate('/new-match'));
      return;
    }

    try {
      setLoading(true);

      // 1. Check local storage cache
      let localCachedState = null;
      try {
        const rawLocal = localStorage.getItem(`local_active_match_${matchId}`);
        if (rawLocal) {
          localCachedState = JSON.parse(rawLocal);
        }
      } catch (e) {}

      let remoteMatch = null;
      let remoteState = null;

      try {
        const res = await trackerFetch(`get_match_details.php?match_id=${matchId}`);
        if (res && res.ok && res.data && res.data.success && res.data.match) {
          remoteMatch = res.data.match;
          if (res.data.current_state) {
            remoteState = typeof res.data.current_state === 'string' ? JSON.parse(res.data.current_state) : res.data.current_state;
          } else if (remoteMatch.current_state) {
            remoteState = typeof remoteMatch.current_state === 'string' ? JSON.parse(remoteMatch.current_state) : remoteMatch.current_state;
          }
        }
      } catch (_e) {
        console.log('Remote match fetch fallback to local storage');
      }

      if (!localCachedState && !remoteMatch) {
        localCachedState = {
          matchData: {
            id: matchId,
            match_id: matchId,
            name: 'Tennis Match',
            match_name: 'Tennis Match',
            player1_id: 'me',
            player1_name: 'Me',
            player2_id: 'opponent',
            player2_name: 'Opponent',
            opponent_name: 'Opponent',
            status: 'inprogress',
            created_at: new Date().toISOString(),
          },
          currentState: {
            setNumber: 1,
            gameNumber: 1,
            myGamesWonInSet: 0,
            opponentGamesWonInSet: 0,
            mySetsWon: 0,
            opponentSetsWon: 0,
            serving: null,
            initialSetServer: null,
            matchScores: [],
            matchCompleted: false,
          },
          pointState: { me: 0, opp: 0 },
          myMistakes: { ace: 0, winner: 0, serveFault: 0, firstServeFault: 0, doubleFault: 0, unforcedError: 0, forcedError: 0 },
          opponentMistakes: { ace: 0, winner: 0, serveFault: 0, firstServeFault: 0, doubleFault: 0, unforcedError: 0, forcedError: 0 },
          serveAttempt: 1,
          completedGames: [],
          updated_at: Date.now(),
        };
      }

      const matchObj = remoteMatch || localCachedState?.matchData || { id: matchId, status: 'inprogress' };
      setMatchData(matchObj);

      const extracted = extractCompletedGames(matchObj);
      setCompletedGames(extracted);

      const computedScores = recalculateScoresFromGames(extracted);
      const isCompleted = matchObj.status === 'completed' || matchObj.status === 'finished' || Boolean(remoteState?.matchCompleted);

      // If remoteMatch has completed games or is completed, remote state MUST take priority
      const hasRemoteGames = Array.isArray(remoteMatch?.sets) && remoteMatch.sets.some((s) => Array.isArray(s.games) && s.games.length > 0);
      const localTs = (localCachedState && localCachedState.updated_at) ? localCachedState.updated_at : 0;
      const remoteTs = (remoteState && remoteState.updated_at) ? remoteState.updated_at : 0;
      const isLocalNewer = (localTs >= remoteTs) && !hasRemoteGames && !isCompleted;

      const chosenState = isLocalNewer 
        ? (localCachedState?.currentState || localCachedState || remoteState) 
        : (remoteState || localCachedState?.currentState || localCachedState);

      const stateCopy = {
        ...chosenState,
        ...computedScores,
        serving: chosenState?.serving || 'me',
        initialSetServer: chosenState?.initialSetServer || 'me',
        matchCompleted: isCompleted,
      };

      setCurrentState(stateCopy);

      const restoredPoints = isCompleted ? { me: 0, opp: 0 } : (chosenState?.pointState || { me: 0, opp: 0 });
      setPointState(restoredPoints);

      let restoredMyM = { ...initialMistakes };
      let restoredOppM = { ...initialMistakes };
      const activeMistakes = chosenState?.activeGameMistakes || (chosenState?.myMistakes ? { me: chosenState?.myMistakes, opponent: chosenState?.opponentMistakes } : null);
      if (activeMistakes && !isCompleted) {
        if (activeMistakes.me) {
          restoredMyM = normalizeMistakes(activeMistakes.me);
          setMyMistakes(restoredMyM);
        }
        if (activeMistakes.opponent) {
          restoredOppM = normalizeMistakes(activeMistakes.opponent);
          setOpponentMistakes(restoredOppM);
        }
      } else {
        setMyMistakes(initialMistakes);
        setOpponentMistakes(initialMistakes);
      }

      const restoredServe = chosenState?.serveAttempt || 1;
      setServeAttempt(restoredServe);

      latestStateRef.current = {
        currentState: stateCopy,
        pointState: restoredPoints,
        myMistakes: restoredMyM,
        opponentMistakes: restoredOppM,
        serveAttempt: restoredServe,
        completedGames: extracted,
        matchData: matchObj,
      };

      const hasServerAlready = Boolean(
        isCompleted ||
        stateCopy.servingSelected || 
        (extracted && extracted.length > 0) || 
        (stateCopy.myGamesWonInSet > 0 || stateCopy.opponentGamesWonInSet > 0) ||
        (stateCopy.mySetsWon > 0 || stateCopy.opponentSetsWon > 0) ||
        (stateCopy.matchScores && stateCopy.matchScores.length > 0)
      );

      if (!hasServerAlready && !stateCopy.matchCompleted && !isCompleted) {
        setShowGameSetupOverlay(true);
      } else {
        setShowGameSetupOverlay(false);
      }
    } catch (error) {
      console.error('Error fetching match details:', error);
      showOverlay(`Error loading match: ${error.message}`, 'error', () => navigate('/history'));
    } finally {
      setLoading(false);
    }
  }, [matchId, navigate, showOverlay, extractCompletedGames, normalizeMistakes, recalculateScoresFromGames]);

  useEffect(() => {
    loadMatch();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [matchId]);

  const selectServer = async (server) => {
    const updated = {
      ...currentState,
      serving: server,
      initialSetServer: server,
      servingSelected: true,
      tbInitialServer: null,
    };
    setCurrentState(updated);
    setShowGameSetupOverlay(false);
    persistInGameState(updated);
  };

  // Match Configuration and Rules Setup
  const matchConfig = currentState.matchConfig || matchData?.match_config || matchData?.matchConfig || {
    numberOfSets: 3,
    gamesPerSet: 6,
    adScoring: 'ad',
    tieBreakAt: 'at_games',
    tieBreakTarget: 7,
    finalSetType: 'super_tiebreak',
    superTieBreakTarget: 10,
  };

  const player1Name = currentState.player1Name || matchConfig.player1Name || matchData?.player1_name || matchData?.player1Name || 'Me';
  const player2Name = currentState.player2Name || matchConfig.player2Name || matchData?.player2_name || matchData?.player2Name || matchData?.opponent_name || matchData?.opponentName || matchData?.opponent || 'Opponent';

  const totalSetsConfig = parseInt(matchConfig.numberOfSets, 10) || 3;
  const setsToWin = Math.ceil(totalSetsConfig / 2);
  const gamesPerSet = parseInt(matchConfig.gamesPerSet, 10) || 6;
  const isNoAd = matchConfig.adScoring === 'no_ad';
  const tbTrigger = matchConfig.tieBreakAt === 'at_minus_one' ? (gamesPerSet - 1) : gamesPerSet;

  // Detect Super Tie-Break set (e.g. at 1:1 for 3 sets or 2:2 for 5 sets)
  const isSuperTieBreakSet = (
    (currentState.mySetsWon === setsToWin - 1 && currentState.opponentSetsWon === setsToWin - 1) &&
    matchConfig.finalSetType === 'super_tiebreak' &&
    (totalSetsConfig === 3 || totalSetsConfig === 5)
  );

  // Detect In-Set Tie-Break (e.g. at 6:6 or 5:5)
  const isInSetTieBreak = !isSuperTieBreakSet && (
    currentState.myGamesWonInSet === tbTrigger &&
    currentState.opponentGamesWonInSet === tbTrigger
  );

  const isTieBreakActive = isSuperTieBreakSet || isInSetTieBreak;
  const tieBreakTargetScore = isSuperTieBreakSet 
    ? (parseInt(matchConfig.superTieBreakTarget, 10) || 10) 
    : (parseInt(matchConfig.tieBreakTarget, 10) || 7);

  const resetMistakes = () => {
    const cleanMistakes = { ...initialMistakes };
    setMyMistakes(cleanMistakes);
    setOpponentMistakes(cleanMistakes);
    setServeAttempt(1);
    persistInGameState(null, null, cleanMistakes, cleanMistakes, 1);
  };

  const updatePointState = (newPoints) => {
    setPointState(newPoints);
    let nextServer = currentState.serving;
    let tbFirstServer = currentState.tbInitialServer;
    if (isTieBreakActive) {
      tbFirstServer = currentState.tbInitialServer || currentState.initialSetServer || currentState.serving || 'me';
      const totalPointsPlayed = (newPoints.me || 0) + (newPoints.opp || 0);
      const isFirstServer = (Math.floor((totalPointsPlayed + 1) / 2) % 2 === 0);
      nextServer = isFirstServer ? tbFirstServer : (tbFirstServer === 'me' ? 'opponent' : 'me');
    }
    const updated = {
      ...currentState,
      pointState: newPoints,
      serving: nextServer,
      ...(tbFirstServer ? { tbInitialServer: tbFirstServer } : {}),
    };
    setCurrentState(updated);
    persistInGameState(updated, newPoints);
  };

  const resetPoints = () => {
    updatePointState({ me: 0, opp: 0 });
  };

  const getPointDisplay = (meP, oppP) => {
    if (isTieBreakActive) {
      let tbStatus = '';
      if (meP >= tieBreakTargetScore - 1 && meP > oppP) {
        tbStatus = isSuperTieBreakSet ? `Match Point (${player1Name})` : `Set Point (${player1Name})`;
      } else if (oppP >= tieBreakTargetScore - 1 && oppP > meP) {
        tbStatus = isSuperTieBreakSet ? `Match Point (${player2Name})` : `Set Point (${player2Name})`;
      }
      return {
        me: String(meP),
        opp: String(oppP),
        status: tbStatus,
      };
    }

    const labels = ['0', '15', '30', '40'];
    if (meP >= 3 && oppP >= 3) {
      if (isNoAd) {
        return { me: '40', opp: '40', status: 'Deciding Point (No-Ad)' };
      }
      if (meP === 3 && oppP === 3) return { me: '40', opp: '40', status: 'Deuce' };
      if (meP === 4 && oppP === 3) return { me: 'Ad', opp: '40', status: `Advantage ${player1Name}` };
      if (meP === 3 && oppP === 4) return { me: '40', opp: 'Ad', status: `Advantage ${player2Name}` };
    }
    return {
      me: labels[meP] || '0',
      opp: labels[oppP] || '0',
      status: '',
    };
  };

  const updateMistake = (player, key, amount) => {
    const item = allMistakeTypes.find((m) => m.key === key);
    if (!item) return;

    // Check server restriction
    const isServer = (player === 'me' && currentState.serving === 'me') || (player === 'opponent' && currentState.serving === 'opponent');
    if (item.serverOnly && !isServer) {
      return;
    }

    if (key === 'serveFault') {
      if (amount > 0) {
        if (serveAttempt === 1) {
          // 1st serve fault -> stats only, advance to 2nd serve
          let newMy = myMistakes;
          let newOpp = opponentMistakes;
          if (player === 'me') {
            newMy = {
              ...myMistakes,
              serveFault: (myMistakes.serveFault || 0) + 1,
              firstServeFault: (myMistakes.firstServeFault || 0) + 1,
            };
            setMyMistakes(newMy);
          } else {
            newOpp = {
              ...opponentMistakes,
              serveFault: (opponentMistakes.serveFault || 0) + 1,
              firstServeFault: (opponentMistakes.firstServeFault || 0) + 1,
            };
            setOpponentMistakes(newOpp);
          }
          setServeAttempt(2);
          persistInGameState(null, null, newMy, newOpp, 2);
        } else {
          // 2nd serve fault -> Double Fault! Point awarded to opponent, reset serveAttempt to 1
          let newMy = myMistakes;
          let newOpp = opponentMistakes;
          if (player === 'me') {
            newMy = {
              ...myMistakes,
              serveFault: (myMistakes.serveFault || 0) + 1,
              doubleFault: (myMistakes.doubleFault || 0) + 1,
            };
            setMyMistakes(newMy);
          } else {
            newOpp = {
              ...opponentMistakes,
              serveFault: (opponentMistakes.serveFault || 0) + 1,
              doubleFault: (opponentMistakes.doubleFault || 0) + 1,
            };
            setOpponentMistakes(newOpp);
          }
          setServeAttempt(1);
          persistInGameState(null, null, newMy, newOpp, 1);
          addPoint(player === 'me' ? 'opponent' : 'me', newMy, newOpp);
        }
      } else if (amount < 0) {
        const curServeFault = player === 'me' ? (myMistakes.serveFault || 0) : (opponentMistakes.serveFault || 0);
        if (curServeFault <= 0) return;

        if (serveAttempt === 2) {
          // Reversing a 1st serve fault
          let newMy = myMistakes;
          let newOpp = opponentMistakes;
          if (player === 'me') {
            newMy = {
              ...myMistakes,
              serveFault: Math.max(0, (myMistakes.serveFault || 0) - 1),
              firstServeFault: Math.max(0, (myMistakes.firstServeFault || 0) - 1),
            };
            setMyMistakes(newMy);
          } else {
            newOpp = {
              ...opponentMistakes,
              serveFault: Math.max(0, (opponentMistakes.serveFault || 0) - 1),
              firstServeFault: Math.max(0, (opponentMistakes.firstServeFault || 0) - 1),
            };
            setOpponentMistakes(newOpp);
          }
          setServeAttempt(1);
          persistInGameState(null, null, newMy, newOpp, 1);
        } else {
          // Reversing a double fault
          const curDoubleFault = player === 'me' ? (myMistakes.doubleFault || 0) : (opponentMistakes.doubleFault || 0);
          if (curDoubleFault > 0) {
            let newMy = myMistakes;
            let newOpp = opponentMistakes;
            if (player === 'me') {
              newMy = {
                ...myMistakes,
                serveFault: Math.max(0, (myMistakes.serveFault || 0) - 1),
                doubleFault: Math.max(0, (myMistakes.doubleFault || 0) - 1),
              };
              setMyMistakes(newMy);
            } else {
              newOpp = {
                ...opponentMistakes,
                serveFault: Math.max(0, (opponentMistakes.serveFault || 0) - 1),
                doubleFault: Math.max(0, (opponentMistakes.doubleFault || 0) - 1),
              };
              setOpponentMistakes(newOpp);
            }
            setServeAttempt(2);
            persistInGameState(null, null, newMy, newOpp, 2);
            subtractPoint(player === 'me' ? 'opponent' : 'me');
          } else {
            let newMy = myMistakes;
            let newOpp = opponentMistakes;
            if (player === 'me') {
              newMy = {
                ...myMistakes,
                serveFault: Math.max(0, (myMistakes.serveFault || 0) - 1),
                firstServeFault: Math.max(0, (myMistakes.firstServeFault || 0) - 1),
              };
              setMyMistakes(newMy);
            } else {
              newOpp = {
                ...opponentMistakes,
                serveFault: Math.max(0, (opponentMistakes.serveFault || 0) - 1),
                firstServeFault: Math.max(0, (opponentMistakes.firstServeFault || 0) - 1),
              };
              setOpponentMistakes(newOpp);
            }
            persistInGameState(null, null, newMy, newOpp, serveAttempt);
          }
        }
      }
      return;
    }

    // Ace, Winner, Unforced Error, Forced Error
    if (amount > 0) {
      let newMy = myMistakes;
      let newOpp = opponentMistakes;
      if (player === 'me') {
        newMy = { ...myMistakes, [key]: (myMistakes[key] || 0) + 1 };
        setMyMistakes(newMy);
      } else {
        newOpp = { ...opponentMistakes, [key]: (opponentMistakes[key] || 0) + 1 };
        setOpponentMistakes(newOpp);
      }

      // Point concludes -> reset to 1st serve
      setServeAttempt(1);
      persistInGameState(null, null, newMy, newOpp, 1);

      if (item.isPositive) {
        addPoint(player, newMy, newOpp);
      } else {
        addPoint(player === 'me' ? 'opponent' : 'me', newMy, newOpp);
      }
    } else if (amount < 0) {
      const currentVal = player === 'me' ? (myMistakes[key] || 0) : (opponentMistakes[key] || 0);
      if (currentVal <= 0) return;

      let newMy = myMistakes;
      let newOpp = opponentMistakes;
      if (player === 'me') {
        newMy = { ...myMistakes, [key]: Math.max(0, (myMistakes[key] || 0) - 1) };
        setMyMistakes(newMy);
      } else {
        newOpp = { ...opponentMistakes, [key]: Math.max(0, (opponentMistakes[key] || 0) - 1) };
        setOpponentMistakes(newOpp);
      }
      persistInGameState(null, null, newMy, newOpp, serveAttempt);

      if (item.isPositive) {
        subtractPoint(player);
      } else {
        subtractPoint(player === 'me' ? 'opponent' : 'me');
      }
    }
  };

  const subtractPoint = (player) => {
    let meP = pointState.me;
    let oppP = pointState.opp;

    if (isTieBreakActive) {
      if (player === 'me') {
        updatePointState({ me: Math.max(0, meP - 1), opp: oppP });
      } else {
        updatePointState({ me: meP, opp: Math.max(0, oppP - 1) });
      }
      return;
    }

    if (player === 'me') {
      if (meP === 0) return;
      if (meP === 4 && oppP === 3) {
        updatePointState({ me: 3, opp: 3 });
        return;
      }
      updatePointState({ me: Math.max(0, meP - 1), opp: oppP });
    } else {
      if (oppP === 0) return;
      if (oppP === 4 && meP === 3) {
        updatePointState({ me: 3, opp: 3 });
        return;
      }
      updatePointState({ me: meP, opp: Math.max(0, oppP - 1) });
    }
  };

  const handleTieBreakWon = async (winner, myFinalPts, oppFinalPts, customMyMistakes = null, customOppMistakes = null) => {
    const playedSetNumber = currentState.setNumber;
    const playedServingPlayer = currentState.serving;
    const playedGameNumber = currentState.gameNumber;
    const lastMyMistakes = { ...(customMyMistakes || latestStateRef.current?.myMistakes || myMistakes) };
    const lastOppMistakes = { ...(customOppMistakes || latestStateRef.current?.opponentMistakes || opponentMistakes) };

    resetPoints();
    resetMistakes();

    if (isSuperTieBreakSet) {
      const finalMySets = currentState.mySetsWon + (winner === 'me' ? 1 : 0);
      const finalOppSets = currentState.opponentSetsWon + (winner === 'opponent' ? 1 : 0);
      const newMatchScores = [
        ...currentState.matchScores,
        { myGames: myFinalPts, opponentGames: oppFinalPts, isSuperTieBreak: true, tbScore: `${myFinalPts}-${oppFinalPts}` }
      ];

      const finishedGame = {
        id: `game_${playedSetNumber}_1_${Date.now()}`,
        setNumber: playedSetNumber,
        gameNumber: 1,
        winner,
        servingPlayer: playedServingPlayer,
        myMistakes: lastMyMistakes,
        opponentMistakes: lastOppMistakes,
        pointScore: `Super TB: ${myFinalPts} - ${oppFinalPts}`,
        isSuperTieBreak: true,
      };

      const stateUpdate = {
        ...currentState,
        mySetsWon: finalMySets,
        opponentSetsWon: finalOppSets,
        matchScores: newMatchScores,
        myGamesWonInSet: myFinalPts,
        opponentGamesWonInSet: oppFinalPts,
        matchCompleted: true,
      };

      setCompletedGames((prev) => [...prev, finishedGame]);
      setCurrentState(stateUpdate);

      try {
        await trackerFetch('save_game.php', {
          method: 'POST',
          body: JSON.stringify({
            matchId,
            setNumber: playedSetNumber,
            gameNumber: 1,
            winner,
            servingPlayer: playedServingPlayer,
            myMistakes: lastMyMistakes,
            opponentMistakes: lastOppMistakes,
            current_match_state: stateUpdate,
          }),
        });

        await trackerFetch('complete_match.php', {
          method: 'POST',
          body: JSON.stringify({
            matchId,
            mySetsWon: finalMySets,
            opponentSetsWon: finalOppSets,
            matchScores: newMatchScores,
          }),
        });
      } catch (e) {
        console.log('Match completed');
      }
      return;
    }

    // In-Set Tie-Break
    const finalMyGames = winner === 'me'
      ? (matchConfig.tieBreakAt === 'at_minus_one' ? gamesPerSet : gamesPerSet + 1)
      : (matchConfig.tieBreakAt === 'at_minus_one' ? gamesPerSet - 1 : gamesPerSet);

    const finalOppGames = winner === 'opponent'
      ? (matchConfig.tieBreakAt === 'at_minus_one' ? gamesPerSet : gamesPerSet + 1)
      : (matchConfig.tieBreakAt === 'at_minus_one' ? gamesPerSet - 1 : gamesPerSet);

    const newMySetsWon = currentState.mySetsWon + (winner === 'me' ? 1 : 0);
    const newOppSetsWon = currentState.opponentSetsWon + (winner === 'opponent' ? 1 : 0);
    const newMatchScores = [
      ...currentState.matchScores,
      { myGames: finalMyGames, opponentGames: finalOppGames, tbScore: `${myFinalPts}-${oppFinalPts}` }
    ];

    const finishedGame = {
      id: `game_${playedSetNumber}_${playedGameNumber}_${Date.now()}`,
      setNumber: playedSetNumber,
      gameNumber: playedGameNumber,
      winner,
      servingPlayer: playedServingPlayer,
      myMistakes: lastMyMistakes,
      opponentMistakes: lastOppMistakes,
      pointScore: `TB: ${myFinalPts} - ${oppFinalPts}`,
      isTieBreak: true,
    };

    setCompletedGames((prev) => [...prev, finishedGame]);

    // Check match win
    if (newMySetsWon >= setsToWin || newOppSetsWon >= setsToWin) {
      const stateUpdate = {
        ...currentState,
        mySetsWon: newMySetsWon,
        opponentSetsWon: newOppSetsWon,
        matchScores: newMatchScores,
        myGamesWonInSet: finalMyGames,
        opponentGamesWonInSet: finalOppGames,
        matchCompleted: true,
      };

      setCurrentState(stateUpdate);

      try {
        await trackerFetch('save_game.php', {
          method: 'POST',
          body: JSON.stringify({
            matchId,
            setNumber: playedSetNumber,
            gameNumber: playedGameNumber,
            winner,
            servingPlayer: playedServingPlayer,
            myMistakes: lastMyMistakes,
            opponentMistakes: lastOppMistakes,
            current_match_state: stateUpdate,
          }),
        });

        await trackerFetch('complete_match.php', {
          method: 'POST',
          body: JSON.stringify({
            matchId,
            mySetsWon: newMySetsWon,
            opponentSetsWon: newOppSetsWon,
            matchScores: newMatchScores,
          }),
        });
      } catch (e) {
        console.log('Match completed');
      }
      return;
    }

    // Advance to next set
    const nextSetServer = playedServingPlayer === 'me' ? 'opponent' : 'me';
    const stateUpdate = {
      ...currentState,
      setNumber: currentState.setNumber + 1,
      gameNumber: 1,
      myGamesWonInSet: 0,
      opponentGamesWonInSet: 0,
      mySetsWon: newMySetsWon,
      opponentSetsWon: newOppSetsWon,
      matchScores: newMatchScores,
      serving: nextSetServer,
      initialSetServer: nextSetServer,
      tbInitialServer: null,
    };

    setCurrentState(stateUpdate);
    setShowGameSetupOverlay(false);

    try {
      await trackerFetch('save_game.php', {
        method: 'POST',
        body: JSON.stringify({
          matchId,
          setNumber: playedSetNumber,
          gameNumber: playedGameNumber,
          winner,
          servingPlayer: playedServingPlayer,
          myMistakes: lastMyMistakes,
          opponentMistakes: lastOppMistakes,
          current_match_state: stateUpdate,
        }),
      });
    } catch (e) {
      console.log('Saved tie-break set to backend');
    }
  };

  const addPoint = (player, customMyMistakes = null, customOppMistakes = null) => {
    let meP = pointState.me;
    let oppP = pointState.opp;

    if (isTieBreakActive) {
      const newMe = player === 'me' ? meP + 1 : meP;
      const newOpp = player === 'opponent' ? oppP + 1 : oppP;

      if (newMe >= tieBreakTargetScore && (newMe - newOpp) >= 2) {
        handleTieBreakWon('me', newMe, newOpp, customMyMistakes, customOppMistakes);
        return;
      }
      if (newOpp >= tieBreakTargetScore && (newOpp - newMe) >= 2) {
        handleTieBreakWon('opponent', newMe, newOpp, customMyMistakes, customOppMistakes);
        return;
      }

      updatePointState({ me: newMe, opp: newOpp });
      return;
    }

    // No-Ad Regular Game Scoring
    if (isNoAd) {
      if (player === 'me') {
        if (meP >= 3) {
          const losScore = oppP === 3 ? '40' : (oppP === 2 ? '30' : (oppP === 1 ? '15' : '0'));
          handleGameWon('me', losScore, customMyMistakes, customOppMistakes);
          return;
        }
        updatePointState({ me: meP + 1, opp: oppP });
      } else {
        if (oppP >= 3) {
          const losScore = meP === 3 ? '40' : (meP === 2 ? '30' : (meP === 1 ? '15' : '0'));
          handleGameWon('opponent', losScore, customMyMistakes, customOppMistakes);
          return;
        }
        updatePointState({ me: meP, opp: oppP + 1 });
      }
      return;
    }

    // Standard With-Advantage Regular Game Scoring
    if (player === 'me') {
      if (meP === 3 && oppP === 3) {
        updatePointState({ me: 4, opp: 3 });
        return;
      }
      if (meP === 3 && oppP === 4) {
        updatePointState({ me: 3, opp: 3 });
        return;
      }
      if ((meP === 4 && oppP === 3) || (meP === 3 && oppP < 3)) {
        const losScore = (oppP === 3 ? '40' : (oppP === 2 ? '30' : (oppP === 1 ? '15' : '0')));
        handleGameWon('me', losScore, customMyMistakes, customOppMistakes);
        return;
      }
      updatePointState({ me: meP + 1, opp: oppP });
    } else {
      if (meP === 3 && oppP === 3) {
        updatePointState({ me: 3, opp: 4 });
        return;
      }
      if (meP === 4 && oppP === 3) {
        updatePointState({ me: 3, opp: 3 });
        return;
      }
      if ((meP === 3 && oppP === 4) || (oppP === 3 && meP < 3)) {
        const losScore = (meP === 3 ? '40' : (meP === 2 ? '30' : (meP === 1 ? '15' : '0')));
        handleGameWon('opponent', losScore, customMyMistakes, customOppMistakes);
        return;
      }
      updatePointState({ me: meP, opp: oppP + 1 });
    }
  };

  const handleGameWon = async (winner, explicitLosingScore = null, customMyMistakes = null, customOppMistakes = null) => {
    const playedSetNumber = currentState.setNumber;
    const playedGameNumber = currentState.gameNumber;
    const playedServingPlayer = currentState.serving;
    const lastMyMistakes = { ...(customMyMistakes || latestStateRef.current?.myMistakes || myMistakes) };
    const lastOppMistakes = { ...(customOppMistakes || latestStateRef.current?.opponentMistakes || opponentMistakes) };

    let newMyGames = currentState.myGamesWonInSet + (winner === 'me' ? 1 : 0);
    let newOppGames = currentState.opponentGamesWonInSet + (winner === 'opponent' ? 1 : 0);

    let losingScoreVal = '0';
    if (explicitLosingScore !== null) {
      losingScoreVal = explicitLosingScore;
    } else {
      const currentPointText = getPointDisplay(pointState.me, pointState.opp);
      if (winner === 'me') {
        losingScoreVal = currentPointText.opp === '0' && currentPointText.me === '0' ? '0' : currentPointText.opp;
      } else {
        losingScoreVal = currentPointText.me === '0' && currentPointText.opp === '0' ? '0' : currentPointText.me;
      }
    }

    const gamePointScore = winner === 'me' ? `40 - ${losingScoreVal}` : `${losingScoreVal} - 40`;

    const finishedGame = {
      id: `game_${playedSetNumber}_${playedGameNumber}_${Date.now()}`,
      setNumber: playedSetNumber,
      gameNumber: playedGameNumber,
      winner,
      servingPlayer: playedServingPlayer,
      myMistakes: lastMyMistakes,
      opponentMistakes: lastOppMistakes,
      pointScore: gamePointScore,
      losingScore: losingScoreVal,
    };

    setCompletedGames((prev) => [...prev, finishedGame]);
    resetMistakes();
    resetPoints();

    // Check if set is won in regular games (e.g. 6-0, 6-4, 7-5)
    const hasWonSet = (
      (winner === 'me' && newMyGames >= gamesPerSet && (newMyGames - newOppGames) >= 2) ||
      (winner === 'opponent' && newOppGames >= gamesPerSet && (newOppGames - newMyGames) >= 2) ||
      (matchConfig.tieBreakAt === 'at_games' && (
        (newMyGames === gamesPerSet + 1 && newOppGames === gamesPerSet - 1) ||
        (newOppGames === gamesPerSet + 1 && newMyGames === gamesPerSet - 1)
      ))
    );

    if (hasWonSet) {
      const newMySetsWon = currentState.mySetsWon + (winner === 'me' ? 1 : 0);
      const newOppSetsWon = currentState.opponentSetsWon + (winner === 'opponent' ? 1 : 0);
      const newMatchScores = [
        ...currentState.matchScores,
        { myGames: newMyGames, opponentGames: newOppGames }
      ];

      if (newMySetsWon >= setsToWin || newOppSetsWon >= setsToWin) {
        const stateUpdate = {
          ...currentState,
          mySetsWon: newMySetsWon,
          opponentSetsWon: newOppSetsWon,
          matchScores: newMatchScores,
          myGamesWonInSet: newMyGames,
          opponentGamesWonInSet: newOppGames,
          matchCompleted: true,
        };

        setCurrentState(stateUpdate);

        try {
          await trackerFetch('save_game.php', {
            method: 'POST',
            body: JSON.stringify({
              matchId,
              setNumber: playedSetNumber,
              gameNumber: playedGameNumber,
              winner,
              servingPlayer: playedServingPlayer,
              myMistakes: lastMyMistakes,
              opponentMistakes: lastOppMistakes,
              current_match_state: stateUpdate,
            }),
          });

          await trackerFetch('complete_match.php', {
            method: 'POST',
            body: JSON.stringify({
              matchId,
              mySetsWon: newMySetsWon,
              opponentSetsWon: newOppSetsWon,
              matchScores: newMatchScores,
            }),
          });
        } catch (e) {
          console.log('Completed match on backend');
        }
        return;
      }

      // Advance to next set!
      const nextSetServer = playedServingPlayer === 'me' ? 'opponent' : 'me';
      const stateUpdate = {
        ...currentState,
        setNumber: currentState.setNumber + 1,
        gameNumber: 1,
        myGamesWonInSet: 0,
        opponentGamesWonInSet: 0,
        mySetsWon: newMySetsWon,
        opponentSetsWon: newOppSetsWon,
        matchScores: newMatchScores,
        serving: nextSetServer,
        initialSetServer: nextSetServer,
        tbInitialServer: null,
      };

      setCurrentState(stateUpdate);
      setShowGameSetupOverlay(false);

      try {
        await trackerFetch('save_game.php', {
          method: 'POST',
          body: JSON.stringify({
            matchId,
            setNumber: playedSetNumber,
            gameNumber: playedGameNumber,
            winner,
            servingPlayer: playedServingPlayer,
            myMistakes: lastMyMistakes,
            opponentMistakes: lastOppMistakes,
            current_match_state: stateUpdate,
          }),
        });
      } catch (e) {
        console.log('Saved set closure to backend');
      }
      return;
    }

    // Next game
    const nextServer = currentState.serving === 'me' ? 'opponent' : 'me';
    const isEnteringTieBreak = (newMyGames === tbTrigger && newOppGames === tbTrigger);
    const stateUpdate = {
      ...currentState,
      myGamesWonInSet: newMyGames,
      opponentGamesWonInSet: newOppGames,
      gameNumber: currentState.gameNumber + 1,
      serving: nextServer,
      tbInitialServer: isEnteringTieBreak ? nextServer : null,
    };

    setCurrentState(stateUpdate);

    try {
      await trackerFetch('save_game.php', {
        method: 'POST',
        body: JSON.stringify({
          matchId,
          setNumber: playedSetNumber,
          gameNumber: playedGameNumber,
          winner,
          servingPlayer: playedServingPlayer,
          myMistakes: lastMyMistakes,
          opponentMistakes: lastOppMistakes,
          current_match_state: stateUpdate,
        }),
      });
    } catch (e) {
      console.log('Saved game data to backend');
    }
  };

  const handleFinishEarly = async () => {
    let stateUpdate = { ...currentState };

    const mg = stateUpdate.myGamesWonInSet;
    const og = stateUpdate.opponentGamesWonInSet;
    if (mg > 0 || og > 0) {
      stateUpdate.matchScores = [...stateUpdate.matchScores, { myGames: mg, opponentGames: og }];
      if (mg > og) stateUpdate.mySetsWon += 1;
      else if (og > mg) stateUpdate.opponentSetsWon += 1;
    }

    stateUpdate.matchCompleted = true;
    setCurrentState(stateUpdate);

    try {
      await trackerFetch('complete_match.php', {
        method: 'POST',
        body: JSON.stringify({
          matchId,
          mySetsWon: stateUpdate.mySetsWon,
          opponentSetsWon: stateUpdate.opponentSetsWon,
          matchScores: stateUpdate.matchScores,
        }),
      });
    } catch (e) {
      console.log('Completed match early on backend');
    }
  };

  // Edit Finished Game Modal Handlers
  const openEditGameModal = (index) => {
    setEditingGameIndex(index);
    setEditingGame(JSON.parse(JSON.stringify(completedGames[index])));
  };

  const updateEditMistake = (player, key, amount) => {
    if (!editingGame) return;
    setEditingGame((prev) => {
      const copy = { ...prev };
      if (player === 'me') {
        const cur = copy.myMistakes?.[key] || 0;
        copy.myMistakes = { ...copy.myMistakes, [key]: Math.max(0, cur + amount) };
      } else {
        const cur = copy.opponentMistakes?.[key] || 0;
        copy.opponentMistakes = { ...copy.opponentMistakes, [key]: Math.max(0, cur + amount) };
      }
      return copy;
    });
  };

  const saveGameEdits = async () => {
    if (editingGameIndex === null || !editingGame) return;

    const updatedList = [...completedGames];
    updatedList[editingGameIndex] = editingGame;
    setCompletedGames(updatedList);

    const recalculated = recalculateScoresFromGames(updatedList);
    const updatedState = {
      ...currentState,
      ...recalculated,
    };

    setCurrentState(updatedState);
    setEditingGameIndex(null);
    setEditingGame(null);

    try {
      await trackerFetch('save_game.php', {
        method: 'POST',
        body: JSON.stringify({
          matchId,
          setNumber: editingGame.setNumber,
          gameNumber: editingGame.gameNumber,
          winner: editingGame.winner,
          servingPlayer: editingGame.servingPlayer,
          myMistakes: editingGame.myMistakes,
          opponentMistakes: editingGame.opponentMistakes,
          current_match_state: updatedState,
        }),
      });
    } catch (e) {
      console.log('Saved game edit to backend');
    }
  };

  const displayPoints = getPointDisplay(pointState.me, pointState.opp);

  if (loading) {
    return <div className="text-center py-12 text-gray-600">Loading match tracker...</div>;
  }

  // Completion Screen
  if (currentState.matchCompleted) {
    let isMeWinner = currentState.mySetsWon > currentState.opponentSetsWon;
    if (currentState.mySetsWon === currentState.opponentSetsWon && Array.isArray(currentState.matchScores)) {
      let totalMyGames = 0;
      let totalOppGames = 0;
      currentState.matchScores.forEach((s) => {
        totalMyGames += parseInt(s.myGames, 10) || 0;
        totalOppGames += parseInt(s.opponentGames, 10) || 0;
      });
      isMeWinner = totalMyGames >= totalOppGames;
    }

    return (
      <div className="bg-white rounded-xl shadow-lg p-8 max-w-2xl mx-auto my-8 text-center space-y-6">
        <h2 className="text-3xl font-extrabold text-gray-800">🏁 Match Complete!</h2>
        <div className={`inline-block px-4 py-1.5 rounded-full text-white font-bold text-lg ${isMeWinner ? 'bg-green-500' : 'bg-red-500'}`}>
          {isMeWinner ? `${player1Name} Won! 🏆` : `${player2Name} Won`}
        </div>

        <div className="bg-gray-50 rounded-lg p-4 max-w-md mx-auto space-y-2 border border-gray-200">
          <p className="text-lg font-bold text-gray-700">Final Sets: {currentState.mySetsWon} - {currentState.opponentSetsWon}</p>
          {currentState.matchScores.length > 0 && (
            <p className="text-gray-600">
              Set Scores: {currentState.matchScores.map((s, idx) => `Set ${idx + 1}: ${s.myGames}-${s.opponentGames}${s.tbScore ? ` (${s.tbScore})` : ''}`).join(' • ')}
            </p>
          )}
        </div>

        <button
          onClick={() => navigate('/dashboard')}
          className="bg-purple-600 hover:bg-purple-700 text-white font-bold py-3 px-8 rounded-lg shadow transition duration-200"
        >
          🏠 Back to Dashboard
        </button>
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto my-6 px-4 space-y-6">
      {/* Streamlined Compact Top Header */}
      <div className="bg-white rounded-xl shadow-md p-4 border border-gray-200">
        {/* Active Tie-Break Banner */}
        {isTieBreakActive && (
          <div className={`mb-3 py-1.5 px-3 rounded-lg text-center font-bold text-xs ${
            isSuperTieBreakSet 
              ? 'bg-amber-100 text-amber-900 border border-amber-300' 
              : 'bg-red-100 text-red-900 border border-red-300'
          }`}>
            {isSuperTieBreakSet 
              ? `⚡ Super Tie-Break (Deciding Set • Target: ${tieBreakTargetScore} pts, win by 2)` 
              : `🔥 Set ${currentState.setNumber} Tie-Break (Target: ${tieBreakTargetScore} pts, win by 2)`}
          </div>
        )}

        <div className="flex justify-between items-start mb-3">
          <div>
            <div className="text-3xl font-extrabold text-gray-900 tracking-tight">
              {isSuperTieBreakSet 
                ? `[STB] ${pointState.me} - ${pointState.opp}`
                : `${currentState.myGamesWonInSet} - ${currentState.opponentGamesWonInSet}`}
            </div>
            <div className="text-xs font-semibold text-gray-500 mt-1">
              Sets: {currentState.mySetsWon}-{currentState.opponentSetsWon} • {isTieBreakActive ? 'Tie-Break' : `Game ${currentState.gameNumber}`} • <span className="text-green-600 font-bold">🎾 {currentState.serving === 'me' ? player1Name : player2Name}</span>
            </div>
          </div>

          <div className="flex flex-col items-end gap-2">
            {currentState.matchScores.length > 0 && (
              <div className="flex gap-1.5 flex-wrap justify-end">
                {currentState.matchScores.map((score, idx) => (
                  <span key={idx} className="bg-gray-100 text-gray-700 text-xs font-bold px-2 py-0.5 rounded border border-gray-300">
                    {score.isSuperTieBreak ? `STB ${score.myGames}-${score.opponentGames}` : `${score.myGames}-${score.opponentGames}${score.tbScore ? ` (${score.tbScore})` : ''}`}
                  </span>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* In-Game Point Tracker Bar */}
        <div className="flex items-center justify-between bg-gray-50 rounded-lg p-2.5 border border-gray-200 mt-2">
          <button
            onClick={() => {
              setTempPoints({ me: pointState.me, opp: pointState.opp });
              setShowPointAdjustOverlay(true);
            }}
            className="flex items-center gap-2 hover:bg-gray-200/70 px-2 py-1 rounded transition duration-150 text-left cursor-pointer"
            title="Click to adjust in-game score"
          >
            <span className="text-xs font-bold text-gray-500 uppercase tracking-wide">{isTieBreakActive ? 'TB Score:' : 'Point:'}</span>
            <span className="text-xl font-extrabold text-green-600 min-w-[28px] text-center">{displayPoints.me}</span>
            <span className="text-gray-400 font-bold">-</span>
            <span className="text-xl font-extrabold text-red-600 min-w-[28px] text-center">{displayPoints.opp}</span>
            {Boolean(displayPoints.status) && (
              <span className="bg-yellow-100 text-yellow-800 text-xs font-bold px-2 py-0.5 rounded-full border border-yellow-300">
                {displayPoints.status}
              </span>
            )}
            <span className="text-xs opacity-60 ml-0.5">✏️</span>
          </button>
        </div>
      </div>
      {/* Unified Table Column Header */}
      <div className="bg-white rounded-xl shadow-sm px-4 py-2.5 border border-gray-200 flex justify-between items-center mb-2.5">
        <span className="font-bold text-gray-700 text-sm flex-1">Event</span>
        <div className="w-32 text-center text-xs font-bold text-green-700">
          {player1Name} 🟢 {currentState.serving === 'me' ? '🎾' : ''}
        </div>
        <div className="w-32 text-center text-xs font-bold text-red-700">
          {player2Name} 🔴 {currentState.serving === 'opponent' ? '🎾' : ''}
        </div>
      </div>

      {/* Serve Mistakes Card */}
      <div className="bg-rose-50/70 rounded-xl shadow-sm px-4 py-2 border border-rose-200 mb-2.5">
        <div className="flex justify-between items-center py-2">
          <span className={`flex-1 text-sm font-semibold ${currentState.serving !== 'me' && currentState.serving !== 'opponent' ? 'text-gray-400' : 'text-gray-800'}`}>
            {serveAttempt === 2 ? 'Double fault' : '1st Serve fault'}
          </span>

          {/* Me Counter */}
          <div className="w-32 flex justify-center items-center gap-1.5">
            <button
              onClick={() => updateMistake('me', 'serveFault', -1)}
              disabled={currentState.serving !== 'me'}
              className={`w-7 h-7 bg-green-100 hover:bg-green-200 text-green-800 rounded-full font-bold text-base flex items-center justify-center transition ${
                currentState.serving !== 'me' ? 'opacity-25 cursor-not-allowed' : ''
              }`}
            >
              -
            </button>
            <span className={`w-7 text-center font-bold text-green-700 text-base ${currentState.serving !== 'me' ? 'opacity-25' : ''}`}>
              {myMistakes.serveFault || 0}
            </span>
            <button
              onClick={() => updateMistake('me', 'serveFault', 1)}
              disabled={currentState.serving !== 'me'}
              className={`w-7 h-7 bg-green-500 hover:bg-green-600 text-white rounded-full font-bold text-base flex items-center justify-center transition ${
                currentState.serving !== 'me' ? 'opacity-25 cursor-not-allowed' : ''
              }`}
            >
              +
            </button>
          </div>

          {/* Opponent Counter */}
          <div className="w-32 flex justify-center items-center gap-1.5">
            <button
              onClick={() => updateMistake('opponent', 'serveFault', -1)}
              disabled={currentState.serving !== 'opponent'}
              className={`w-7 h-7 bg-red-100 hover:bg-red-200 text-red-800 rounded-full font-bold text-base flex items-center justify-center transition ${
                currentState.serving !== 'opponent' ? 'opacity-25 cursor-not-allowed' : ''
              }`}
            >
              -
            </button>
            <span className={`w-7 text-center font-bold text-red-700 text-base ${currentState.serving !== 'opponent' ? 'opacity-25' : ''}`}>
              {opponentMistakes.serveFault || 0}
            </span>
            <button
              onClick={() => updateMistake('opponent', 'serveFault', 1)}
              disabled={currentState.serving !== 'opponent'}
              className={`w-7 h-7 bg-red-500 hover:bg-red-600 text-white rounded-full font-bold text-base flex items-center justify-center transition ${
                currentState.serving !== 'opponent' ? 'opacity-25 cursor-not-allowed' : ''
              }`}
            >
              +
            </button>
          </div>
        </div>
      </div>

      {/* Positive Shots Card */}
      <div className="bg-emerald-50/70 rounded-xl shadow-sm px-4 py-2 border border-emerald-200 mb-2.5">
        <div className="divide-y divide-emerald-100">
          {positiveEvents.map((m) => {
            const isMeServer = currentState.serving === 'me';
            const isOppServer = currentState.serving === 'opponent';
            const meDisabled = m.serverOnly && !isMeServer;
            const oppDisabled = m.serverOnly && !isOppServer;

            return (
              <div key={m.key} className="flex justify-between items-center py-2">
                <span className={`flex-1 text-sm font-semibold ${meDisabled && oppDisabled ? 'text-gray-400' : 'text-gray-800'}`}>
                  {m.label}
                </span>

                {/* Me Counter */}
                <div className="w-32 flex justify-center items-center gap-1.5">
                  <button
                    onClick={() => updateMistake('me', m.key, -1)}
                    disabled={meDisabled}
                    className={`w-7 h-7 bg-green-100 hover:bg-green-200 text-green-800 rounded-full font-bold text-base flex items-center justify-center transition ${
                      meDisabled ? 'opacity-25 cursor-not-allowed' : ''
                    }`}
                  >
                    -
                  </button>
                  <span className={`w-7 text-center font-bold text-green-700 text-base ${meDisabled ? 'opacity-25' : ''}`}>
                    {myMistakes[m.key] || 0}
                  </span>
                  <button
                    onClick={() => updateMistake('me', m.key, 1)}
                    disabled={meDisabled}
                    className={`w-7 h-7 bg-green-500 hover:bg-green-600 text-white rounded-full font-bold text-base flex items-center justify-center transition ${
                      meDisabled ? 'opacity-25 cursor-not-allowed' : ''
                    }`}
                  >
                    +
                  </button>
                </div>

                {/* Opponent Counter */}
                <div className="w-32 flex justify-center items-center gap-1.5">
                  <button
                    onClick={() => updateMistake('opponent', m.key, -1)}
                    disabled={oppDisabled}
                    className={`w-7 h-7 bg-red-100 hover:bg-red-200 text-red-800 rounded-full font-bold text-base flex items-center justify-center transition ${
                      oppDisabled ? 'opacity-25 cursor-not-allowed' : ''
                    }`}
                  >
                    -
                  </button>
                  <span className={`w-7 text-center font-bold text-red-700 text-base ${oppDisabled ? 'opacity-25' : ''}`}>
                    {opponentMistakes[m.key] || 0}
                  </span>
                  <button
                    onClick={() => updateMistake('opponent', m.key, 1)}
                    disabled={oppDisabled}
                    className={`w-7 h-7 bg-red-500 hover:bg-red-600 text-white rounded-full font-bold text-base flex items-center justify-center transition ${
                      oppDisabled ? 'opacity-25 cursor-not-allowed' : ''
                    }`}
                  >
                    +
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Errors & Mistakes Card */}
      <div className="bg-rose-50/70 rounded-xl shadow-sm px-4 py-2 border border-rose-200 mb-3">
        <div className="divide-y divide-rose-100">
          {negativeEvents.map((m) => {
            const isMeServer = currentState.serving === 'me';
            const isOppServer = currentState.serving === 'opponent';
            const meDisabled = m.serverOnly && !isMeServer;
            const oppDisabled = m.serverOnly && !isOppServer;

            return (
              <div key={m.key} className="flex justify-between items-center py-2">
                <div className="flex-1 flex items-center gap-2">
                  <span className={`text-sm font-semibold ${meDisabled && oppDisabled ? 'text-gray-400' : 'text-gray-800'}`}>
                    {m.label}
                  </span>
                </div>

                {/* Me Counter */}
                <div className="w-32 flex justify-center items-center gap-1.5">
                  <button
                    onClick={() => updateMistake('me', m.key, -1)}
                    disabled={meDisabled}
                    className={`w-7 h-7 bg-green-100 hover:bg-green-200 text-green-800 rounded-full font-bold text-base flex items-center justify-center transition ${
                      meDisabled ? 'opacity-25 cursor-not-allowed' : ''
                    }`}
                  >
                    -
                  </button>
                  <span className={`w-7 text-center font-bold text-green-700 text-base ${meDisabled ? 'opacity-25' : ''}`}>
                    {myMistakes[m.key] || 0}
                  </span>
                  <button
                    onClick={() => updateMistake('me', m.key, 1)}
                    disabled={meDisabled}
                    className={`w-7 h-7 bg-green-500 hover:bg-green-600 text-white rounded-full font-bold text-base flex items-center justify-center transition ${
                      meDisabled ? 'opacity-25 cursor-not-allowed' : ''
                    }`}
                  >
                    +
                  </button>
                </div>

                {/* Opponent Counter */}
                <div className="w-32 flex justify-center items-center gap-1.5">
                  <button
                    onClick={() => updateMistake('opponent', m.key, -1)}
                    disabled={oppDisabled}
                    className={`w-7 h-7 bg-red-100 hover:bg-red-200 text-red-800 rounded-full font-bold text-base flex items-center justify-center transition ${
                      oppDisabled ? 'opacity-25 cursor-not-allowed' : ''
                    }`}
                  >
                    -
                  </button>
                  <span className={`w-7 text-center font-bold text-red-700 text-base ${oppDisabled ? 'opacity-25' : ''}`}>
                    {opponentMistakes[m.key] || 0}
                  </span>
                  <button
                    onClick={() => updateMistake('opponent', m.key, 1)}
                    disabled={oppDisabled}
                    className={`w-7 h-7 bg-red-500 hover:bg-red-600 text-white rounded-full font-bold text-base flex items-center justify-center transition ${
                      oppDisabled ? 'opacity-25 cursor-not-allowed' : ''
                    }`}
                  >
                    +
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Finished Games Horizontal Scroll Section */}
      {completedGames.length > 0 && (
        <div className="bg-white rounded-xl shadow-md p-4 border border-gray-200">
          <div className="flex justify-between items-center mb-1">
            <h3 className="font-bold text-gray-800 text-sm">Finished Games ({completedGames.length})</h3>
            <span className="text-xs text-gray-500">Tap any game card to modify</span>
          </div>

          <div className="flex gap-3 overflow-x-auto pb-2 pt-2 scrollbar-thin">
            {completedGames.map((game, index) => {
              const isMe = game.winner === 'me' || game.winner === 'player1';
              const p1M = Object.values(game.myMistakes || {}).reduce((sum, v) => sum + (parseInt(v, 10) || 0), 0);
              const p2M = Object.values(game.opponentMistakes || {}).reduce((sum, v) => sum + (parseInt(v, 10) || 0), 0);

              return (
                <div
                  key={index}
                  onClick={() => openEditGameModal(index)}
                  className={`flex-shrink-0 w-48 p-3 rounded-lg border-2 cursor-pointer transition transform hover:scale-[1.02] ${
                    isMe ? 'bg-green-50 border-green-300' : 'bg-red-50 border-red-300'
                  }`}
                >
                  <div className="flex justify-between items-center mb-1">
                    <span className="font-bold text-xs text-gray-800">
                      Set {game.setNumber} • {game.isSuperTieBreak ? 'Super TB' : (game.isTieBreak ? 'Tie-Break' : `Game ${game.gameNumber}`)} ({game.pointScore || (isMe ? '40-0' : '0-40')})
                    </span>
                    <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded text-white ${isMe ? 'bg-green-600' : 'bg-red-600'}`}>
                      {isMe ? 'Won' : 'Lost'}
                    </span>
                  </div>
                  <div className="text-xs text-gray-700">
                    Winner: <strong className={isMe ? 'text-green-700' : 'text-red-700'}>{isMe ? `${player1Name} 🟢` : `${player2Name} 🔴`}</strong>
                  </div>
                  <div className="text-[11px] text-gray-500 mt-1">
                    Mistakes: {p1M} {player1Name} / {p2M} {player2Name}
                  </div>
                  <div className="text-xs font-bold text-blue-600 mt-2 text-right">✏️ Edit Game</div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Subtle Finish Match Early / Retire */}
      <div className="text-center py-2">
        <button
          type="button"
          onClick={() => {
            if (window.confirm('Are you sure you want to end this match early? Unfinished games will be closed with current scores.')) {
              handleFinishEarly();
            }
          }}
          className="text-xs font-medium text-gray-400 hover:text-gray-600 underline transition"
        >
          🏳️ End Match Early / Retire
        </button>
      </div>

      {/* Adjust In-Game Point Score Modal */}
      {showPointAdjustOverlay && (
        <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-sm w-full p-6 space-y-4">
            <div className="text-center border-b pb-2">
              <h3 className="text-xl font-bold text-gray-800">
                {isTieBreakActive ? (isSuperTieBreakSet ? 'Adjust Super Tie-Break' : 'Adjust Tie-Break Points') : 'Adjust Game Score'}
              </h3>
              <p className="text-xs text-gray-500 mt-0.5">
                Set {currentState.setNumber} • {isTieBreakActive ? `Target: ${tieBreakTargetScore} pts` : `Game ${currentState.gameNumber}`}
              </p>
            </div>

            {/* Player 1 Score */}
            <div>
              <label className="block text-xs font-bold text-green-700 uppercase mb-1.5">{player1Name} 🟢 {isTieBreakActive ? 'Points' : 'Point'}:</label>
              {isTieBreakActive ? (
                <div className="grid grid-cols-6 gap-1.5">
                  {[0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12].map(pVal => (
                    <button
                      key={pVal}
                      onClick={() => setTempPoints((p) => ({ ...p, me: pVal }))}
                      className={`py-1.5 rounded-lg font-bold text-xs border transition ${
                        tempPoints.me === pVal
                          ? 'bg-green-500 text-white border-green-500 shadow-sm'
                          : 'bg-gray-50 text-gray-700 border-gray-200 hover:bg-gray-100'
                      }`}
                    >
                      {pVal}
                    </button>
                  ))}
                </div>
              ) : (
                <div className="grid grid-cols-5 gap-1.5">
                  {[
                    { label: '0', val: 0 },
                    { label: '15', val: 1 },
                    { label: '30', val: 2 },
                    { label: '40', val: 3 },
                    { label: 'Ad', val: 4 },
                  ].map((opt) => (
                    <button
                      key={opt.label}
                      onClick={() => setTempPoints((p) => ({ ...p, me: opt.val }))}
                      className={`py-2 rounded-lg font-bold text-sm border transition ${
                        tempPoints.me === opt.val
                          ? 'bg-green-500 text-white border-green-500 shadow-sm'
                          : 'bg-gray-50 text-gray-700 border-gray-200 hover:bg-gray-100'
                      }`}
                    >
                      {opt.label}
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Player 2 Score */}
            <div>
              <label className="block text-xs font-bold text-red-700 uppercase mb-1.5">{player2Name} 🔴 {isTieBreakActive ? 'Points' : 'Point'}:</label>
              {isTieBreakActive ? (
                <div className="grid grid-cols-6 gap-1.5">
                  {[0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12].map(pVal => (
                    <button
                      key={pVal}
                      onClick={() => setTempPoints((p) => ({ ...p, opp: pVal }))}
                      className={`py-1.5 rounded-lg font-bold text-xs border transition ${
                        tempPoints.opp === pVal
                          ? 'bg-red-500 text-white border-red-500 shadow-sm'
                          : 'bg-gray-50 text-gray-700 border-gray-200 hover:bg-gray-100'
                      }`}
                    >
                      {pVal}
                    </button>
                  ))}
                </div>
              ) : (
                <div className="grid grid-cols-5 gap-1.5">
                  {[
                    { label: '0', val: 0 },
                    { label: '15', val: 1 },
                    { label: '30', val: 2 },
                    { label: '40', val: 3 },
                    { label: 'Ad', val: 4 },
                  ].map((opt) => (
                    <button
                      key={opt.label}
                      onClick={() => setTempPoints((p) => ({ ...p, opp: opt.val }))}
                      className={`py-2 rounded-lg font-bold text-sm border transition ${
                        tempPoints.opp === opt.val
                          ? 'bg-red-500 text-white border-red-500 shadow-sm'
                          : 'bg-gray-50 text-gray-700 border-gray-200 hover:bg-gray-100'
                      }`}
                    >
                      {opt.label}
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Actions */}
            <div className="flex gap-2 pt-2">
              <button
                onClick={() => {
                  updatePointState({ me: tempPoints.me, opp: tempPoints.opp });
                  setShowPointAdjustOverlay(false);
                }}
                className="flex-1 bg-blue-600 hover:bg-blue-700 text-white font-bold py-2.5 rounded-lg shadow transition duration-150 text-sm"
              >
                Save Point Score
              </button>
              <button
                onClick={() => setShowPointAdjustOverlay(false)}
                className="bg-gray-200 hover:bg-gray-300 text-gray-800 font-bold py-2.5 px-4 rounded-lg shadow transition duration-150 text-sm"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Initial Server Selection Overlay */}
      {showGameSetupOverlay && (
        <GameSetupOverlay
          onSelectServer={selectServer}
          onClose={() => setShowGameSetupOverlay(false)}
          player1Name={player1Name}
          player2Name={player2Name}
          opponentName={player2Name}
        />
      )}

      {/* Edit Finished Game Modal */}
      {editingGameIndex !== null && editingGame && (
        <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-lg w-full p-6 max-h-[90vh] overflow-y-auto space-y-4">
            <h3 className="text-xl font-bold text-gray-800 border-b pb-2">
              Edit Set {editingGame.setNumber} - Game {editingGame.gameNumber}
            </h3>

            {/* Set Assignment */}
            <div>
              <label className="block text-xs font-bold text-gray-600 uppercase mb-1">Set Assignment:</label>
              <div className="flex gap-2 flex-wrap">
                {[1, 2, 3, 4, 5].map((sNum) => (
                  <button
                    key={sNum}
                    onClick={() => setEditingGame((prev) => ({ ...prev, setNumber: sNum }))}
                    className={`px-3 py-1 rounded-md text-xs font-bold border transition ${
                      editingGame.setNumber === sNum ? 'bg-blue-600 text-white border-blue-600' : 'bg-gray-100 text-gray-700 border-gray-300'
                    }`}
                  >
                    Set {sNum}
                  </button>
                ))}
              </div>
            </div>

            {/* Winner Selection */}
            <div>
              <label className="block text-xs font-bold text-gray-600 uppercase mb-1">Game Winner:</label>
              <div className="grid grid-cols-2 gap-3">
                <button
                  onClick={() =>
                    setEditingGame((prev) => ({
                      ...prev,
                      winner: 'me',
                      pointScore: `40 - ${prev.losingScore || 0}`,
                    }))
                  }
                  className={`py-2 px-3 rounded-lg font-bold text-sm border transition ${
                    editingGame.winner === 'me' ? 'bg-green-500 text-white border-green-500' : 'bg-gray-100 text-gray-700 border-gray-300'
                  }`}
                >
                  {player1Name} 🟢
                </button>
                <button
                  onClick={() =>
                    setEditingGame((prev) => ({
                      ...prev,
                      winner: 'opponent',
                      pointScore: `${prev.losingScore || 0} - 40`,
                    }))
                  }
                  className={`py-2 px-3 rounded-lg font-bold text-sm border transition ${
                    editingGame.winner === 'opponent' ? 'bg-red-500 text-white border-red-500' : 'bg-gray-100 text-gray-700 border-gray-300'
                  }`}
                >
                  {player2Name} 🔴
                </button>
              </div>
            </div>

            {/* Losing Side Point Score */}
            <div>
              <label className="block text-xs font-bold text-gray-600 uppercase mb-1">Losing Side Point Score:</label>
              <div className="grid grid-cols-4 gap-2">
                {['0', '15', '30', '40'].map((scoreVal) => (
                  <button
                    key={scoreVal}
                    onClick={() => {
                      const isMe = editingGame.winner === 'me';
                      const newPtScore = isMe ? `40 - ${scoreVal}` : `${scoreVal} - 40`;
                      setEditingGame((prev) => ({
                        ...prev,
                        losingScore: scoreVal,
                        pointScore: newPtScore,
                      }));
                    }}
                    className={`py-1.5 rounded-md font-bold text-xs border transition ${
                      String(editingGame.losingScore || '0') === scoreVal
                        ? 'bg-green-600 text-white border-green-600'
                        : 'bg-gray-100 text-gray-700 border-gray-300'
                    }`}
                  >
                    {scoreVal}
                  </button>
                ))}
              </div>
            </div>

            {/* Mistakes & Shots in this Game */}
            <div>
              <label className="block text-xs font-bold text-gray-600 uppercase mb-2">Game Shots & Mistakes:</label>
              <div className="space-y-2 max-h-48 overflow-y-auto pr-1">
                {allMistakeTypes.map((m) => (
                  <div key={m.key} className="flex justify-between items-center text-xs py-1 border-b border-gray-100">
                    <span className="font-medium text-gray-700">{m.label}</span>
                    <div className="flex gap-4 items-center">
                      <div className="flex items-center gap-1">
                        <button
                          onClick={() => updateEditMistake('me', m.key, -1)}
                          className="w-5 h-5 bg-green-100 text-green-800 rounded font-bold"
                        >
                          -
                        </button>
                        <span className="w-5 text-center font-bold text-green-700">{editingGame.myMistakes?.[m.key] || 0}</span>
                        <button
                          onClick={() => updateEditMistake('me', m.key, 1)}
                          className="w-5 h-5 bg-green-500 text-white rounded font-bold"
                        >
                          +
                        </button>
                      </div>
                      <div className="flex items-center gap-1">
                        <button
                          onClick={() => updateEditMistake('opponent', m.key, -1)}
                          className="w-5 h-5 bg-red-100 text-red-800 rounded font-bold"
                        >
                          -
                        </button>
                        <span className="w-5 text-center font-bold text-red-700">{editingGame.opponentMistakes?.[m.key] || 0}</span>
                        <button
                          onClick={() => updateEditMistake('opponent', m.key, 1)}
                          className="w-5 h-5 bg-red-500 text-white rounded font-bold"
                        >
                          +
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Modal Actions */}
            <div className="flex gap-3 pt-3 border-t">
              <button
                onClick={saveGameEdits}
                className="flex-1 bg-green-500 hover:bg-green-600 text-white font-bold py-2.5 rounded-lg shadow transition"
              >
                Save Changes
              </button>
              <button
                onClick={() => {
                  setEditingGameIndex(null);
                  setEditingGame(null);
                }}
                className="flex-1 bg-gray-300 hover:bg-gray-400 text-gray-800 font-bold py-2.5 rounded-lg transition"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MatchTrackerPage;
